import React, { useState, useEffect, useContext } from 'react';
import { Typography, Table, Button, Input, InputNumber, Switch, Space, Select, Tag, Popconfirm, Modal, Form, Divider, Checkbox, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import WeeklyHoursEditor, { hoursToForm, formToHours, hoursSummary } from '../components/WeeklyHoursEditor';
import { UserContext } from '../App';

// Admin > Help Desk Queues: queues and the people who work them. Only a
// queue's members appear in a ticket's Assigned To list; a member's home team
// is not involved. Removing someone leaves tickets already assigned to them.
//
// A Restricted queue (HR, whose tickets hold PII) shows each ticket only to its
// members and the ticket's own people (who raised it, its impacted employee,
// its watchers -- added by members only): supervisors' team access does not
// reach it. So who is in it is the access list: only an admin can turn
// Restricted on or off or change its members, and each change goes to the
// security log and to the recipients on the Alert Config page.
export default function HelpDeskQueuesPage() {
  const currentUser = useContext(UserContext);
  const isAdmin = currentUser?.Role === 'admin';
  const [queues,  setQueues]  = useState([]);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding,  setAdding]  = useState({});        // queueID -> [userIDs] being added
  const [modal,   setModal]   = useState(null);      // null | {id?, name}
  const [saving,  setSaving]  = useState(false);
  const [form] = Form.useForm();
  const prefix = Form.useWatch('ticket_prefix', form);

  // Asset types (for a ticket's Impacted Assets and Shipments)
  const [assetTypes,  setAssetTypes]  = useState([]);
  const [typeModal,   setTypeModal]   = useState(null);   // null | {id?}
  const [typeSaving,  setTypeSaving]  = useState(false);
  const [typeForm] = Form.useForm();

  const loadTypes = async () => {
    try {
      const res = await api.get('/api/helpdesk/asset-types');
      setAssetTypes(res.data || []);
    } catch {
      message.error('Failed to load asset types.');
    }
  };

  const openTypeModal = (t) => {
    setTypeModal(t ? { id: t.ID } : {});
    typeForm.setFieldsValue({ name: t ? t.Name : '', description: t ? (t.Description || '') : '', is_standard: !!t?.IsStandard });
  };

  const saveTypeModal = async () => {
    let v;
    try { v = await typeForm.validateFields(); } catch { return; }
    setTypeSaving(true);
    try {
      const body = { name: v.name, description: v.description || null, is_standard: !!v.is_standard };
      if (typeModal.id) await api.put(`/api/helpdesk/asset-types/${typeModal.id}`, body);
      else              await api.post('/api/helpdesk/asset-types', body);
      setTypeModal(null);
      loadTypes();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setTypeSaving(false);
    }
  };

  const toggleTypeActive = async (t) => {
    try {
      await api.put(`/api/helpdesk/asset-types/${t.ID}`, { is_active: !t.IsActive });
      loadTypes();
    } catch {
      message.error('Failed to update.');
    }
  };

  const typeColumns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 60 },
    { title: 'Name', dataIndex: 'Name', key: 'Name', width: 220, sorter: (a, b) => a.Name.localeCompare(b.Name) },
    { title: 'Description', dataIndex: 'Description', key: 'Description',
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Standard Equipment', dataIndex: 'IsStandard', key: 'IsStandard', width: 150,
      sorter: (a, b) => (a.IsStandard ? 1 : 0) - (b.IsStandard ? 1 : 0),
      render: (v) => (v ? <Tag color="blue">Standard</Tag> : <Typography.Text type="secondary">—</Typography.Text>) },
    { title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 90,
      render: (v, t) => <Switch size="small" checked={!!v} onChange={() => toggleTypeActive(t)} /> },
    { title: '', key: 'edit', width: 70, align: 'right',
      render: (_, t) => <Button size="small" icon={<EditOutlined />} onClick={() => openTypeModal(t)}>Edit</Button> },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/helpdesk/queues');
      setQueues(res.data);
    } catch {
      message.error('Failed to load queues.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadTypes();
    api.get('/api/helpdesk/filter-options').then((r) => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  const openModal = (q) => {
    setModal(q ? { id: q.ID } : {});
    form.setFieldsValue({
      name: q ? q.Name : '',
      ticket_prefix: q?.TicketPrefix || '',
      after_hours_message: q?.AfterHoursMessage || '',
      hours: hoursToForm(q?.OperatingHours),
      refresh_seconds: q?.RefreshSeconds ?? 30,
    });
  };

  const saveModal = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const { hours, error } = formToHours(v.hours);
      if (error) { message.error(error); setSaving(false); return; }
      const body = { name: v.name, operating_hours: hours,
                     after_hours_message: v.after_hours_message || '',
                     refresh_seconds: v.refresh_seconds };
      // The prefix: set when the queue is made; after that only an admin may change it.
      if (modal.id && isAdmin) body.ticket_prefix = v.ticket_prefix;
      if (modal.id) await api.put(`/api/helpdesk/queues/${modal.id}`, body);
      else {
        const res = await api.post('/api/helpdesk/queues', { name: v.name, ticket_prefix: v.ticket_prefix });
        await api.put(`/api/helpdesk/queues/${res.data.id}`, body);
      }
      setModal(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (q) => {
    try {
      await api.put(`/api/helpdesk/queues/${q.ID}`, { is_active: !q.IsActive });
      load();
    } catch {
      message.error('Failed to update.');
    }
  };

  const addMembers = async (q) => {
    const ids = adding[q.ID] || [];
    if (!ids.length) return;
    try {
      await api.post(`/api/helpdesk/queues/${q.ID}/members`, { user_ids: ids });
      setAdding((prev) => ({ ...prev, [q.ID]: [] }));
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add.');
    }
  };

  // Email on create: this member is emailed when a ticket lands in the queue.
  const setEmailOnCreate = async (q, m, on) => {
    try {
      await api.put(`/api/helpdesk/queues/${q.ID}/members/${m.ID}`, { email_on_create: on });
      load();
    } catch {
      message.error('Failed to update.');
    }
  };

  const removeMember = async (q, userId) => {
    try {
      await api.delete(`/api/helpdesk/queues/${q.ID}/members/${userId}`);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to remove.');
    }
  };

  // Add Team: the team's people are added once, as individuals. There is no
  // lasting link to the team: one would let anyone who can change a person's
  // team give them the queue's tickets.
  const [addingTeam, setAddingTeam] = useState({});
  const teamOptions = [...new Map(users.filter((u) => u.TeamID).map((u) => [u.TeamID, u.TeamName])).entries()]
    .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''))
    .map(([value, label]) => ({ value, label }));
  const addTeam = async (q, memberIds) => {
    const ids = users.filter((u) => u.TeamID === addingTeam[q.ID] && !memberIds.has(u.ID)).map((u) => u.ID);
    if (!ids.length) { message.info('Everyone on that team is already a member.'); return; }
    try {
      await api.post(`/api/helpdesk/queues/${q.ID}/members`, { user_ids: ids });
      setAddingTeam((prev) => ({ ...prev, [q.ID]: undefined }));
      message.success(`Added ${ids.length} ${ids.length === 1 ? 'person' : 'people'}.`);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add.');
    }
  };

  const setRestricted = async (q, on) => {
    try {
      await api.put(`/api/helpdesk/queues/${q.ID}`, { is_restricted: on });
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update.');
    }
  };

  const columns = [
    { title: 'Queue', dataIndex: 'Name', key: 'Name', width: 220,
      sorter: (a, b) => a.Name.localeCompare(b.Name),
      render: (v, q) => (
        <>
          {v}
          {!!q.IsRestricted && (
            <Tooltip title="Its tickets are seen only by its members and by the person who raised, is impacted by or watches each one">
              <Tag color="red" style={{ marginLeft: 8 }}>Restricted</Tag>
            </Tooltip>
          )}
        </>
      ) },
    { title: 'Prefix', dataIndex: 'TicketPrefix', key: 'TicketPrefix', width: 90,
      sorter: (a, b) => (a.TicketPrefix || '').localeCompare(b.TicketPrefix || ''),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    {
      title: 'Members', key: 'Members',
      render: (_, q) => {
        const memberIds = new Set((q.Members || []).map((m) => m.ID));
        const locked = !!q.IsRestricted && !isAdmin;
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Space wrap size={[4, 4]}>
              {(q.Members || []).length === 0 && <Typography.Text type="secondary">No members</Typography.Text>}
              {(q.Members || []).map((m) => (
                <Tag key={m.ID} color={m.IsActive ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
                  {m.DisplayName}{m.TeamName ? ` (${m.TeamName})` : ''}{!m.IsActive && ' (inactive)'}
                  <Checkbox checked={!!m.EmailOnCreate} style={{ marginLeft: 8, fontSize: 12 }}
                    onChange={(e) => setEmailOnCreate(q, m, e.target.checked)}>
                    Email on create
                  </Checkbox>
                  {!locked && (
                    <Popconfirm title={`Remove ${m.DisplayName} from ${q.Name}?`} okText="Remove"
                                onConfirm={() => removeMember(q, m.ID)}>
                      <DeleteOutlined style={{ marginLeft: 6, cursor: 'pointer' }} />
                    </Popconfirm>
                  )}
                </Tag>
              ))}
            </Space>
            {locked ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Only an admin can change who is in a restricted queue.
              </Typography.Text>
            ) : (
              <Space wrap>
                <Select
                  mode="multiple" showSearch optionFilterProp="label" placeholder="Add people"
                  style={{ minWidth: 320 }}
                  value={adding[q.ID] || []}
                  onChange={(v) => setAdding((prev) => ({ ...prev, [q.ID]: v }))}
                  options={users.filter((u) => !memberIds.has(u.ID))
                    .map((u) => ({ value: u.ID, label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName }))}
                />
                <Button onClick={() => addMembers(q)} disabled={!(adding[q.ID] || []).length}>Add</Button>
                <Select
                  showSearch optionFilterProp="label" placeholder="Add a team" allowClear
                  style={{ minWidth: 180 }}
                  value={addingTeam[q.ID]}
                  onChange={(v) => setAddingTeam((prev) => ({ ...prev, [q.ID]: v }))}
                  options={teamOptions}
                />
                <Tooltip title="Adds the team's people now, one by one; people who join the team later are not added">
                  <Button onClick={() => addTeam(q, memberIds)} disabled={!addingTeam[q.ID]}>Add Team</Button>
                </Tooltip>
              </Space>
            )}
          </Space>
        );
      },
    },
    { title: 'Operating Hours', key: 'Hours', width: 230,
      render: (_, q) => {
        const parts = hoursSummary(q.OperatingHours);
        return parts ? parts.map((p) => <div key={p} style={{ fontSize: 12 }}>{p}</div>)
          : <Typography.Text type="secondary" style={{ fontSize: 12 }}>Always open</Typography.Text>;
      } },
    { title: 'Restricted', dataIndex: 'IsRestricted', key: 'IsRestricted', width: 110,
      render: (v, q) => (
        <Tooltip title={isAdmin ? null : 'Only an admin can change this'}>
          <Popconfirm
            disabled={!isAdmin}
            title={v ? `Turn Restricted off for ${q.Name}?` : `Restrict ${q.Name}?`}
            description={v
              ? "Supervisors with Help Desk View will see their teams' tickets in it again."
              : "Only its members, and each ticket's creator, impacted employee and watchers, will see its tickets. Supervisors' team access and admins are not exempt."}
            okText={v ? 'Turn off' : 'Restrict'}
            onConfirm={() => setRestricted(q, !v)}
          >
            <Switch size="small" checked={!!v} disabled={!isAdmin} />
          </Popconfirm>
        </Tooltip>
      ) },
    { title: 'Updates', dataIndex: 'RefreshSeconds', key: 'RefreshSeconds', width: 110,
      render: (v) => <span style={{ fontSize: 12 }}>every {v}s</span> },
    { title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 90,
      render: (v, q) => <Switch size="small" checked={!!v} onChange={() => toggleActive(q)} /> },
    { title: '', key: 'edit', width: 80, align: 'right',
      render: (_, q) => <Button size="small" icon={<EditOutlined />} onClick={() => openModal(q)}>Edit</Button> },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Help Desk Queues</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>Add Queue</Button>
      </div>
      <Table rowKey="ID" dataSource={queues} columns={columns} loading={loading} size="small" pagination={false}
             locale={{ emptyText: 'No queues yet.' }} />
      <Divider />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Asset Types</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openTypeModal(null)}>Add Asset Type</Button>
      </div>
      <Table rowKey="ID" dataSource={assetTypes} columns={typeColumns} size="small" pagination={false}
             locale={{ emptyText: 'No asset types yet.' }} />

      <Modal maskClosable={false} title={typeModal?.id ? 'Edit Asset Type' : 'New Asset Type'} open={!!typeModal} onOk={saveTypeModal}
             onCancel={() => setTypeModal(null)} confirmLoading={typeSaving} okText="Save" destroyOnHidden>
        <Form form={typeForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="is_standard" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>Standard Equipment</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal maskClosable={false} title={modal?.id ? 'Edit Queue' : 'New Queue'} open={!!modal} onOk={saveModal}
             onCancel={() => setModal(null)} confirmLoading={saving} okText="Save" destroyOnHidden width={560}>
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input maxLength={100} />
          </Form.Item>
          {/* Every ticket's key starts with it (HDIT-325). Unique across queues; the
              server checks that too. Tickets keep their key if it changes later. */}
          <Form.Item label="Ticket Prefix" name="ticket_prefix"
                     normalize={(v) => (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '')}
                     extra={modal?.id && !isAdmin ? 'Only an admin can change the prefix.'
                       : modal?.id ? `New tickets will be ${prefix || '…'}-…; existing tickets keep their keys.`
                         : `Tickets will look like ${prefix || 'HDIT'}-1.`}
                     rules={[{ required: true, message: 'Required' },
                             { pattern: /^[A-Z][A-Z0-9]{1,5}$/, message: '2 to 6 letters or digits, starting with a letter' }]}>
            <Input maxLength={6} disabled={!!modal?.id && !isAdmin} style={{ width: 140 }} />
          </Form.Item>
          <Form.Item label="Operating Hours (Eastern)" style={{ marginBottom: 8 }}>
            <WeeklyHoursEditor name="hours" offLabel="Closed" />
          </Form.Item>
          <Form.Item label="After-Hours Message" name="after_hours_message">
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
          {/* How often open Help Desk pages check this queue's tickets for
              changes: a few seconds where techs race for tickets. */}
          <Form.Item label="Update Speed" name="refresh_seconds"
                     extra="How often open Help Desk pages show changes to this queue's tickets (2 to 600 seconds)."
                     rules={[{ required: true, message: 'Required' }]}>
            <InputNumber min={2} max={600} precision={0} addonAfter="seconds" style={{ width: 180 }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
